// frontend/src/components/personalizer/WalletPassPreview.jsx
// Apple Wallet-style pass preview for the card designer.
// Mimics the real storeCard layout that gets pushed to Apple Wallet.

import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../../utils/i18n';
import { normalizeLogoUrl } from '../../utils/urlHelper';

function isDark(hex) {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
}

export default function WalletPassPreview({
    bgColor = '#1a73e8',
    textColor: textColorProp = null,
    stampIcon = '⭐',
    stampGoal = 10,
    currentCount = 0,
    rewardText = 'Free reward!',
    cardTitle = 'Loyalty Card',
    logoUrl = null,
    clientName = 'John Doe',
    stampColor = 'rgba(255,255,255,0.25)',
    numberColor = 'rgba(255,255,255,0.3)',
    headerMode = 'title',
}) {
    const { t } = useLanguage();
    const dark = isDark(bgColor);
    const textColor = textColorProp || (dark ? '#ffffff' : '#000000');
    const mutedColor = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
    const circleBorder = dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';
    const emptyCircleBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

    // Normalize logo URL
    const normalizedLogo = normalizeLogoUrl(logoUrl);

    const remaining = Math.max(0, stampGoal - currentCount);

    const cols = 5;

    return (
        <div style={{
            width: '100%',
            maxWidth: 340,
            borderRadius: 16,
            overflow: 'hidden',
            background: bgColor,
            boxShadow: '0 12px 40px -10px rgba(0,0,0,0.5)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
        }}>
            {/* ─── Top: logoText (business name) ─── */}
            <div style={{
                padding: '16px 24px 8px',
            }}>
                {headerMode === 'logo' && normalizedLogo ? (
                    <img
                        key={normalizedLogo}
                        src={normalizedLogo}
                        alt=""
                        style={{
                            height: 30,
                            borderRadius: 4,
                            objectFit: 'contain',
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: textColor,
                        letterSpacing: -0.3,
                        textTransform: 'uppercase',
                    }}>
                        {cardTitle}
                    </span>
                )}
            </div>

            {/* ─── Strip area: centered stamps ─── */}
            <div style={{
                padding: '12px 24px 16px',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 8,
            }}>
                {Array.from({ length: stampGoal }).map((_, i) => {
                    const filled = i < currentCount;
                    const circleSize = stampGoal <= 5 ? 52 : stampGoal <= 10 ? 44 : 38;
                    return (
                        <div
                            key={i}
                            style={{
                                width: circleSize,
                                height: circleSize,
                                borderRadius: '50%',
                                background: filled ? stampColor : emptyCircleBg,
                                border: `2px solid ${filled ? stampColor : circleBorder}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: filled ? (circleSize > 44 ? 24 : 20) : 14,
                                fontWeight: 500,
                                color: filled ? 'inherit' : (numberColor || mutedColor),
                                flexShrink: 0,
                            }}
                        >
                            {filled ? stampIcon : i + 1}
                        </div>
                    );
                })}
            </div>

            {/* ─── Secondary Fields: UNTIL REWARD | NAME ─── */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 24px 12px',
            }}>
                <div>
                    <p style={{
                        margin: 0,
                        fontSize: 10,
                        fontWeight: 600,
                        color: mutedColor,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}>
                        {t('until_reward')}
                    </p>
                    <p style={{
                        margin: '2px 0 0',
                        fontSize: 22,
                        fontWeight: 400,
                        color: textColor,
                    }}>
                        {remaining > 0 ? remaining : t('ready')}
                    </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{
                        margin: 0,
                        fontSize: 10,
                        fontWeight: 600,
                        color: mutedColor,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}>
                        {t('name')}
                    </p>
                    <p style={{
                        margin: '2px 0 0',
                        fontSize: 22,
                        fontWeight: 400,
                        color: textColor,
                    }}>
                        {clientName}
                    </p>
                </div>
            </div>

            {/* ─── QR Code ─── */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '8px 24px 32px',
            }}>
                <div style={{
                    background: '#ffffff',
                    borderRadius: 12,
                    padding: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                    <QRCodeSVG
                        value={`${window.location.origin}/scan-preview`}
                        size={150}
                        level="H"
                        includeMargin={false}
                    />
                </div>
            </div>
        </div>
    );
}
